// "Activated cities" — where a partner is switched on (migration 0239).
//
// Lives in its own file, and SAVES ITSELF, for one reason: activation is not a
// property of the form around it. The main editor Save PATCHes a whole draft,
// so folding activation into it would mean that changing a commission rate
// re-sends the city list — and a list that was stale by a minute (someone else
// activated Lyon meanwhile) would quietly take a city away. A separate PUT
// makes "these cities and no others" a thing ops did on purpose.
//
// Two shapes, one control: `all` (everywhere published, the flag) hides the
// city list entirely, because a list underneath a ticked Everywhere box reads
// as a restriction it is not. Saving it also CLEARS the stored cities — the
// two are never both populated — so the hint says so before the click rather
// than leaving ops to discover it when they come back off Everywhere and find
// an empty list. DriveCars' own partners are locked into it —
// every customer on drivecars.ai shops through one of those rows, and the API
// refuses `all: false` for them with a 409 regardless of what this UI does.
//
// The arithmetic (which country box is on, what the counts are) is in
// partner-locations-state.ts so it can be tested without a DOM.

import { useEffect, useMemo, useState } from 'react';
import {
  fetchAdminLocations,
  fetchPartnerLocations,
  savePartnerLocations,
  type AdminLocationRow,
} from '@/lib/admin-api';
import { ApiError } from '@/lib/api';
import {
  activationCounts,
  isCountryFullySelected,
  toggleCity,
  toggleCountry,
  type ActivatableCity,
} from './partner-locations-state';

export interface PartnerLocationsFieldsetProps {
  partnerId: number;
  /** DriveCars' own storefront/app rows. Everywhere is locked ON for them —
   *  shown disabled rather than hidden so ops can see WHY it cannot be
   *  changed instead of wondering where the control went. */
  isDriveCarsPartner: boolean;
}

/** Every published city at once, grouped for rendering. A paged picker could
 *  not answer "all in France", which is the control ops actually reaches for,
 *  so the API's list limit was raised to 500 for exactly this call. */
const CITY_PAGE_LIMIT = 500;

interface CountryGroup {
  code: string;
  label: string;
  cities: ActivatableCity[];
}

function groupByCountry(cities: ActivatableCity[]): CountryGroup[] {
  const groups = new Map<string, CountryGroup>();
  for (const city of cities) {
    const code = (city.countryCode ?? '').toLowerCase();
    // A row with no country code would otherwise vanish into an unlabelled
    // group nobody can tick; it gets its own honest heading instead.
    const key = code || 'unknown';
    let group = groups.get(key);
    if (!group) {
      group = {
        code: key,
        // The country's NAME when the list carried one, else the code — the
        // admin locations list joins `en` names per row and a city row does
        // not carry its country's name today, so this is usually the code.
        label: city.countryName?.trim() || (code ? code.toUpperCase() : 'No country'),
        cities: [],
      };
      groups.set(key, group);
    }
    if (city.countryName?.trim()) group.label = city.countryName.trim();
    group.cities.push(city);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function toCity(row: AdminLocationRow): ActivatableCity {
  return {
    id: row.id,
    // Slug is the fallback, not an empty label: an unnamed row must still be
    // identifiable enough to tick or leave alone.
    name: row.name?.trim() || row.slug,
    countryCode: row.countryCode,
  };
}

export function PartnerLocationsFieldset({
  partnerId,
  isDriveCarsPartner,
}: PartnerLocationsFieldsetProps) {
  const [all, setAll] = useState(isDriveCarsPartner);
  const [selected, setSelected] = useState<number[]>([]);
  const [cities, setCities] = useState<ActivatableCity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([
      fetchPartnerLocations(partnerId),
      // Asked for even when `all` is on: unticking Everywhere must not then
      // wait on a round-trip before showing anything to tick.
      fetchAdminLocations({ type: 'city', status: 'published', limit: CITY_PAGE_LIMIT }),
    ])
      .then(([scope, list]) => {
        if (!live) return;
        setAll(scope.all);
        setSelected(scope.cityIds);
        setCities((list.rows ?? []).map(toCity));
        setError(null);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : 'Could not load activated cities.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [partnerId]);

  const groups = useMemo(() => groupByCountry(cities), [cities]);
  const counts = activationCounts(cities, selected);

  const summary = all
    ? 'Everywhere published'
    : `${counts.cities} ${counts.cities === 1 ? 'city' : 'cities'} in ${counts.countries} ${
        counts.countries === 1 ? 'country' : 'countries'
      }`;

  const handleSave = () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    // `all` is always sent, `cityIds` only when it is the thing being set.
    // A list alongside `all: true` would contradict the server, which CLEARS
    // the city rows when the flag goes on — the response then comes back with
    // `cityIds: []` and setSelected below drops the local ticks to match, so
    // unticking Everywhere starts from nothing rather than from a set the
    // database no longer holds.
    savePartnerLocations(partnerId, all ? { all: true } : { all: false, cityIds: selected })
      .then((res) => {
        setAll(res.all);
        setSelected(res.cityIds);
        setSaved(true);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string } | undefined;
          if (body?.error === 'first_party_everywhere') {
            setError('DriveCars’ own partners are activated everywhere and cannot be narrowed.');
            return;
          }
          if (body?.error === 'not_a_city') {
            setError('Only cities can be activated — one of the selected rows is not one.');
            return;
          }
        }
        setError(err instanceof Error ? err.message : 'Could not save activated cities.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <fieldset>
      <legend className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
        Activated cities
      </legend>
      <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
        Where this partner may sell. Activating a city also activates the airports and stations under it.
        Saved on its own — the Save below does not touch the rest of this form.
      </p>

      <label className="mt-1.5 flex items-center gap-1.5 text-[13px]">
        <input
          type="checkbox"
          aria-label="Everywhere"
          checked={all}
          disabled={isDriveCarsPartner || loading}
          onChange={() => {
            setAll((v) => !v);
            setSaved(false);
          }}
        />
        <span>Everywhere</span>
      </label>
      {isDriveCarsPartner ? (
        <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
          This is DriveCars’ own partner row — it is activated everywhere published and cannot be narrowed.
        </p>
      ) : (
        // Said before the click, not discovered after it: saving Everywhere
        // deletes the city rows server-side, so coming back off it starts from
        // nothing rather than from the set that was there a moment ago.
        <p className="mt-1 text-[11.5px] text-[color:var(--bo-mute)]">
          Turning this on clears the city list.
        </p>
      )}

      {loading ? (
        <p className="mt-2 text-[12px] text-[color:var(--bo-mute)]">Loading cities…</p>
      ) : null}

      {!all && !loading ? (
        <div className="mt-2 max-h-72 space-y-3 overflow-y-auto rounded-xl border border-[color:var(--bo-line)] p-3">
          {groups.length === 0 ? (
            <p className="text-[12px] text-[color:var(--bo-mute)]">No published cities to activate.</p>
          ) : (
            groups.map((group) => (
              <div key={group.code}>
                <label className="flex items-center gap-1.5 text-[12px] font-semibold">
                  <input
                    type="checkbox"
                    aria-label={`All in ${group.label}`}
                    checked={isCountryFullySelected(cities, selected, group.code)}
                    onChange={() => {
                      setSelected((cur) => toggleCountry(cities, cur, group.code));
                      setSaved(false);
                    }}
                  />
                  <span>All in {group.label}</span>
                </label>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 pl-5">
                  {group.cities.map((city) => (
                    <label key={city.id} className="flex items-center gap-1.5 text-[13px]">
                      <input
                        type="checkbox"
                        aria-label={city.name}
                        checked={selected.includes(city.id)}
                        onChange={() => {
                          setSelected((cur) => toggleCity(cur, city.id));
                          setSaved(false);
                        }}
                      />
                      <span>{city.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}

      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={saving || loading}
          onClick={handleSave}
          className="inline-flex h-9 shrink-0 items-center rounded-lg bg-[color:var(--bo-ink)] px-3 text-[12px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save activated cities'}
        </button>
        <span className="text-[11.5px] text-[color:var(--bo-mute)]">{summary}</span>
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-[11.5px] text-[#9f1239]">
          {error}
        </p>
      ) : null}
      {saved && !error ? (
        <p role="status" className="mt-2 text-[11.5px] text-[#166534]">
          Activated cities saved.
        </p>
      ) : null}
    </fieldset>
  );
}
